(() => {
  const PRICING = {
    minimumKits: 5,
    maximumKits: 35,
    liveAssembly: 2000,
    wristbandPackPrice: 150,
    wristbandsPerPack: 50
  };

  const FUNDING_PATHS = {
    budget: {
      label: "School / District Budget",
      text: "A purchase order against athletics, activities, or student-support funds. Boards meet monthly and approve purchases as routine agenda items. We provide board-ready documentation on request."
    },
    boosters: {
      label: "Booster Club",
      text: "Booster and activity funds typically sit outside district procurement—often the fastest path, with no board vote required."
    },
    sponsor: {
      label: "Local Business Sponsor",
      text: "A business funds the program and is recognized on the custom team patch in every athlete’s kit—visible community goodwill for them, with no budget impact for the school."
    },
    donors: {
      label: "Donors",
      text: "Community supporters, alumni, and families can help fund a program. Ask us about the best way to structure a gift for your school."
    }
  };

  function escapeHtml(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function money(value) {
    const number = Number(value) || 0;

    return `$${Math.round(number).toLocaleString("en-US")}`;
  }

  function formatDate(value) {
    if (!value) return "";

    const date = new Date(`${value}T12:00:00`);

    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  }

  function exactKitPrice(kits) {
    if (kits <= 10) {
      return 2500 + (kits - 5) * 200;
    }

    return 3500 + (kits - 10) * 160;
  }

  function initializeBuilder(root) {
    if (!root || root.dataset.fqbInitialized === "true") return;

    root.dataset.fqbInitialized = "true";

    const field = (name) =>
      root.querySelector(`[data-fqb-field="${name}"]`);

    const output = (name) =>
      root.querySelector(`[data-fqb-output="${name}"]`);

    const setText = (name, value) => {
      const element = output(name);

      if (element) {
        element.textContent = value;
      }
    };

    if (root.dataset.hideThemeChrome === "true") {
      document.body.classList.add("fqb-hide-theme-chrome");
    }

    const validField = field("valid");

    if (validField && !validField.value) {
      const validDate = new Date();
      validDate.setDate(validDate.getDate() + 30);
      validField.value = validDate.toISOString().slice(0, 10);
    }

    function getPricing() {
      const enteredRoster =
        parseInt(field("roster")?.value, 10) || PRICING.minimumKits;

      const roster = Math.min(
        PRICING.maximumKits,
        Math.max(PRICING.minimumKits, enteredRoster)
      );

      const coaches = Math.max(
        1,
        parseInt(field("coaches")?.value, 10) || 1
      );

      const wristbandPacks = Math.max(
        0,
        parseInt(field("wristbands")?.value, 10) || 0
      );

      const isFullProgram = roster === 35;
      const includedManuals = isFullProgram ? 2 : roster;
      const basePrice = exactKitPrice(roster);

      const includedDepartmentBands =
        roster >= 10 ? (isFullProgram ? 250 : 100) : 0;

      const extraDepartmentBands =
        wristbandPacks * PRICING.wristbandsPerPack;

      const totalDepartmentBands =
        includedDepartmentBands + extraDepartmentBands;

      const wristbandCost =
        wristbandPacks * PRICING.wristbandPackPrice;

      const selectedKickoff = root.querySelector(
        '[data-fqb-field="kickoff"]:checked'
      );

      const assembly =
        selectedKickoff?.value === "assembly"
          ? PRICING.liveAssembly
          : 0;

      const standard = basePrice + wristbandCost + assembly;
      const offeredField = field("offered");
      const offeredValue = offeredField?.value.trim() ?? "";
      const parsedOffered = Number(offeredValue);

      const hasCustomOffer =
        offeredValue !== "" &&
        Number.isFinite(parsedOffered) &&
        parsedOffered >= 0;

      const offered = hasCustomOffer ? parsedOffered : standard;
      const adjustment = offered - standard;

      return {
        enteredRoster,
        roster,
        coaches,
        wristbandPacks,
        kits: roster,
        includedManuals,
        basePrice,
        includedDepartmentBands,
        extraDepartmentBands,
        totalDepartmentBands,
        wristbandCost,
        assembly,
        standard,
        offered,
        adjustment,
        hasCustomOffer,
        athleteBands: roster * 7,
        isFullProgram,
        packageName: isFullProgram
          ? "Full Leadership Program"
          : `${roster} Kit Custom Package`
      };
    }

    function renderCalculation(pricing) {
      const element = output("calculation");

      if (!element) return;

      let html = `
        <div class="fqb-calculation__line">
          <span>
            ${escapeHtml(pricing.packageName)}
            (${pricing.kits} kits +
            ${pricing.includedManuals}
            Coach Book${pricing.includedManuals === 1 ? "" : "s"})
          </span>
          <span>${money(pricing.basePrice)}</span>
        </div>
      `;

      if (pricing.wristbandPacks > 0) {
        html += `
          <div class="fqb-calculation__line">
            <span>
              ${pricing.wristbandPacks}
              extra wristband pack${pricing.wristbandPacks === 1 ? "" : "s"}
            </span>
            <span>${money(pricing.wristbandCost)}</span>
          </div>
        `;
      }

      if (pricing.assembly > 0) {
        html += `
          <div class="fqb-calculation__line">
            <span>Live kickoff assembly + travel</span>
            <span>${money(pricing.assembly)}</span>
          </div>
        `;
      }

      html += `
        <div class="fqb-calculation__line">
          <span>Standard investment</span>
          <span>${money(pricing.standard)}</span>
        </div>
      `;

      if (pricing.adjustment < 0) {
        html += `
          <div class="fqb-calculation__line fqb-calculation__line--discount">
            <span>Program discount</span>
            <span>− ${money(Math.abs(pricing.adjustment))}</span>
          </div>
        `;
      }

      if (pricing.adjustment > 0) {
        html += `
          <div class="fqb-calculation__line">
            <span>Custom price adjustment</span>
            <span>+ ${money(pricing.adjustment)}</span>
          </div>
        `;
      }

      html += `
        <div class="fqb-calculation__line fqb-calculation__line--total">
          <span>Quote total</span>
          <span>${money(pricing.offered)}</span>
        </div>

        <div class="fqb-calculation__line">
          <span>Per athlete (${pricing.roster})</span>
          <span>${money(pricing.offered / pricing.roster)}</span>
        </div>
      `;

      if (pricing.enteredRoster > PRICING.maximumKits) {
        html += `
          <div class="fqb-calculation__line fqb-calculation__line--discount">
            <span>Rosters above 35 require a custom quote.</span>
            <span></span>
          </div>
        `;
      }

      element.innerHTML = html;
    }

    function renderQuoteTable(pricing, school) {
      const table = output("quoteTable");

      if (!table) return;

      let rows = `
        <thead>
          <tr>
            <th>Item</th>
            <th>Details</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>FAMILY ${escapeHtml(pricing.packageName)}</strong></td>
            <td>
              ${pricing.kits} curriculum kits,
              ${pricing.includedManuals}
              Coach${pricing.includedManuals === 1 ? "" : "es"}
              Manual${pricing.includedManuals === 1 ? "" : "s"},
              custom team patch, and
              ${pricing.includedDepartmentBands}
              FAMILY department wristbands
            </td>
            <td>${money(pricing.basePrice)}</td>
          </tr>
      `;

      if (pricing.wristbandPacks > 0) {
        rows += `
          <tr>
            <td><strong>Extra Department Wristbands</strong></td>
            <td>
              ${pricing.wristbandPacks}
              pack${pricing.wristbandPacks === 1 ? "" : "s"} of
              ${PRICING.wristbandsPerPack}
              (${pricing.totalDepartmentBands} department bands total)
            </td>
            <td>${money(pricing.wristbandCost)}</td>
          </tr>
        `;
      }

      if (pricing.assembly > 0) {
        rows += `
          <tr>
            <td><strong>Live Kickoff Assembly</strong></td>
            <td>
              Mike or Bob launches the program at an all-athletes assembly.
              Travel is billed separately after scheduling.
            </td>
            <td>${money(pricing.assembly)}</td>
          </tr>
        `;
      } else {
        rows += `
          <tr>
            <td><strong>Kickoff Video</strong></td>
            <td>
              The professionally produced FAMILY kickoff video launches the program.
            </td>
            <td>Included</td>
          </tr>
        `;
      }

      if (pricing.hasCustomOffer) {
        rows += `
          <tr>
            <td><strong>Standard Investment</strong></td>
            <td></td>
            <td>${money(pricing.standard)}</td>
          </tr>
        `;

        if (pricing.adjustment < 0) {
          rows += `
            <tr class="fqb-table-discount">
              <td><strong>Program Discount</strong></td>
              <td>Prepared for ${escapeHtml(school)}</td>
              <td>− ${money(Math.abs(pricing.adjustment))}</td>
            </tr>
          `;
        }

        if (pricing.adjustment > 0) {
          rows += `
            <tr>
              <td><strong>Custom Price Adjustment</strong></td>
              <td>Prepared for ${escapeHtml(school)}</td>
              <td>+ ${money(pricing.adjustment)}</td>
            </tr>
          `;
        }
      }

      rows += `
          <tr class="fqb-table-total">
            <td>Total</td>
            <td>
              About ${money(pricing.offered / pricing.roster)} per athlete
              for a season of leadership development
            </td>
            <td>${money(pricing.offered)}</td>
          </tr>
        </tbody>
      `;

      table.innerHTML = rows;
    }

    function renderFunding() {
      const fundingElement = output("fundingRows");
      const selectedFunding = field("funding")?.value || "all";

      if (!fundingElement) return;

      const keys =
        selectedFunding === "all"
          ? Object.keys(FUNDING_PATHS)
          : [selectedFunding];

      let html = keys
        .map((key) => {
          const path = FUNDING_PATHS[key];

          if (!path) return "";

          return `
            <div class="fqb-funding-row">
              <span class="fqb-funding-row__label">
                ${escapeHtml(path.label)}
              </span>
              <span class="fqb-funding-row__text">
                ${escapeHtml(path.text)}
              </span>
            </div>
          `;
        })
        .join("");

      if (selectedFunding !== "all") {
        html += `
          <div class="fqb-funding-row">
            <span class="fqb-funding-row__label">Also Available</span>
            <span class="fqb-funding-row__text">
              School budgets, boosters, sponsors, and donors can all work—and
              they can be combined. Ask us about the other options anytime.
            </span>
          </div>
        `;
      }

      fundingElement.innerHTML = html;
    }

    function updateBuilder() {
      const pricing = getPricing();

      const school = field("school")?.value.trim() || "Your School";
      const contact = field("contact")?.value.trim() || "—";
      const mascot = field("mascot")?.value.trim() || "";
      const sport = field("sport")?.value.trim() || "";
      const mascotSport = [mascot, sport].filter(Boolean).join(" | ");

      const startDate = field("start")?.value || "";
      const finishDate = field("finish")?.value || "";
      const validDate = field("valid")?.value || "";
      const publicNotes = field("publicNotes")?.value || "";

      const formattedStart = formatDate(startDate);
      const formattedFinish = formatDate(finishDate);
      const formattedValid =
        formatDate(validDate) || "30 days from the quote date";

      const quoteDate = new Date().toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric"
      });

      renderCalculation(pricing);
      renderQuoteTable(pricing, school);
      renderFunding();

      setText("coverSchool", school);
      setText("coverContact", contact);
      setText("coverMascotSport", mascotSport || " ");
      setText("quoteDate", quoteDate);
      setText("coverValid", formattedValid);

      setText("rosterHeading", pricing.roster);
      setText("schoolHeading", school);
      setText("quoteValid", formattedValid);
      setText("startDateShort", formattedStart || "this fall");
      setText("publicNotes", publicNotes);

      if (formattedStart || formattedFinish) {
        setText(
          "programDates",
          `Target start: ${formattedStart || "TBD"}. ` +
            `Season finish: ${formattedFinish || "TBD"}. ` +
            "Next season’s cohort renews with additional kits only."
        );
      } else {
        setText(
          "programDates",
          "Start and finish dates to be confirmed together on approval."
        );
      }

      const athletesOutput = output("includedAthletes");

      if (athletesOutput) {
        athletesOutput.innerHTML = `
          <strong>${pricing.kits} complete curriculum kits</strong>
          for your roster of ${pricing.roster}. Each includes a workbook,
          seven wristbands—six traits plus the FAMILY band,
          ${pricing.athleteBands} athlete wristbands in total—a notebook,
          pen, and premium zipper pouch with your custom team patch.
        `;
      }

      const coachesOutput = output("includedCoaches");

      if (coachesOutput) {
        coachesOutput.innerHTML = `
          <strong>
            ${pricing.includedManuals}
            Coach${pricing.includedManuals === 1 ? "" : "es"}
            Manual${pricing.includedManuals === 1 ? "" : "s"}
          </strong>
          for your staff of ${pricing.coaches}. The full season-long SALT
          curriculum includes discussions, activities, and the weekly rhythm
          that turns culture into a habit.
        `;
      }

      const departmentOutput = output("includedDepartment");

      if (departmentOutput) {
        departmentOutput.innerHTML = `
          <strong>
            ${pricing.totalDepartmentBands} FAMILY department wristbands
          </strong>
          so the standard is visible on every team in the building.
        `;
      }

      setText(
        "includedKickoff",
        pricing.assembly > 0
          ? "A live all-athletes kickoff assembly with Mike or Bob—the moment the whole school remembers."
          : "The professionally produced FAMILY kickoff video launches your program—every school starts with a moment."
      );
    }

    root.addEventListener("input", updateBuilder);
    root.addEventListener("change", updateBuilder);

    const printButton = root.querySelector('[data-fqb-action="print"]');

    printButton?.addEventListener("click", () => {
      updateBuilder();

      const schoolName =
        field("school")?.value.trim() || "School";

      const safeSchoolName = schoolName
        .replace(/[^a-z0-9 ]/gi, "")
        .trim()
        .replace(/\s+/g, "-");

      const originalTitle = document.title;
      document.title = `FAMILY-Quote-${safeSchoolName}`;

      window.print();

      document.title = originalTitle;
    });

    updateBuilder();
  }

  function initializeAllBuilders(container = document) {
    container
      .querySelectorAll("[data-family-quote-builder]")
      .forEach(initializeBuilder);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initializeAllBuilders();
    });
  } else {
    initializeAllBuilders();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initializeAllBuilders(event.target);
  });
})();